import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import Select from "react-select";
import LoadingIndicator from "../Components/LoadingIndicator";

const UserReport = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [appliedRole, setAppliedRole] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchUsers();
  }, [currentPage, appliedSearch, limit, appliedRole, sortBy, sortOrder]);

  const fetchUsers = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        action: "listUser",
        page: String(currentPage),
        limit: String(limit),
        sortBy,
        sortOrder,
      };

      if (appliedSearch.trim()) {
        payload.search = appliedSearch.trim();
      }

      if (appliedRole?.value) {
        payload.role = appliedRole.value;
      }

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = response.data;
      setRows(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      setRoles(data.roles || []);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load users";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteUser = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }

    const userId = selectedRow?._id;
    if (!userId) {
      toast.error("User ID missing.");
      return;
    }

    setIsDeleting(true);
    try {
      const res = await axios.post(API_URL, {
        action: "deleteUser",
        id: userId,
      });

      toast.success(res.data?.message || "User deleted successfully");
      setRows((prev) => prev.filter((row) => row._id !== userId));

      closeDeleteModal();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete user",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleExport = async () => {
    try {
      const payload = {
        action: "userExcel",
        search: appliedSearch || undefined,
      };

      const res = await axios.post(API_URL, payload);

      const base64 = res.data.body;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      const link = document.createElement("a");
      link.href = url;
      link.download = `users-report-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to export users");
    }
  };

  const roleOptions = roles.map((role) => ({
    label: role.charAt(0).toUpperCase() + role.slice(1),
    value: role,
  }));

  const getRoleColor = (role) => {
    if (!role) return { bg: "#e9ecef", text: "#495057" };

    let hash = 0;
    for (let i = 0; i < role.length; i++) {
      hash = role.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);

    const lightness = 80 + (hash % 10);
    const saturation = 60 + (hash % 20);

    return {
      bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      text: `hsl(${hue}, 70%, 25%)`,
    };
  };

  const getName = (row) => row.name || "-";
  const getEmail = (row) => row.email || "-";
  const getPhone = (row) => row.phone || "-";
  const getCompanyName = (row) => row.companyname || "-";
  const getRole = (row) => {
    if (!row.role) return "-";
    return row.role.charAt(0).toUpperCase() + row.role.slice(1);
  };
  const getStatus = (row) => row.status || "-";
  const getPlan = (row) => row.plan || "-";
  const getCity = (row) => row.city || "-";
  const getState = (row) => row.state || "-";

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const handleApplyFilter = () => {
    setAppliedRole(selectedRole);
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setSelectedRole(null);
    setAppliedRole(null);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("createdAt");
      setSortOrder("desc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            opacity: 0.35,
            marginLeft: "5px",
          }}
        ></i>
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    );
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>User Report</h1>
            <p>View all registered users.</p>
          </div>
          <div className="col-sm-6 text-right">
            <button
              className="btn btn-success"
              onClick={handleExport}
              disabled={isLoading}
            >
              <i className="fas fa-file-excel mr-2"></i> Export
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row mb-3 align-items-end">
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-12 mb-2">
              <form onSubmit={handleSearch}>
                <label className="font-weight-bold mb-1">Search</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="col-md-3">
              <Select
                options={roleOptions}
                value={selectedRole}
                onChange={setSelectedRole}
                placeholder="Select role..."
                isClearable={false}
                classNamePrefix="react-select"
              />
            </div>
            <div className="col-md-4 text-end">
              <button
                className="btn btn-primary me-2"
                onClick={handleApplyFilter}
                disabled={isLoading}
              >
                Apply
              </button>

              {selectedRole && (
                <button
                  className="btn btn-secondary"
                  onClick={handleClearFilter}
                  disabled={isLoading}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No user data found.</div>
          ) : (
            <>
              <div className="employee-table-wrapper">
                <table className="table table-borderless employee-table subscription-table">
                  <thead style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                    <tr>
                      <th>Sl No.</th>

                      <th
                        onClick={() => handleSort("name")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Name {renderSortIcon("name")}
                      </th>

                      <th
                        onClick={() => handleSort("email")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Email {renderSortIcon("email")}
                      </th>

                      <th>
                        Phone 
                      </th>

                      <th
                        onClick={() => handleSort("companyname")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Company {renderSortIcon("companyname")}
                      </th>

                      <th
                        onClick={() => handleSort("role")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Role {renderSortIcon("role")}
                      </th>

                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Status {renderSortIcon("status")}
                      </th>

                      <th
                        onClick={() => handleSort("plan")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Plan {renderSortIcon("plan")}
                      </th>

                      <th
                        onClick={() => handleSort("city")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        City {renderSortIcon("city")}
                      </th>

                      <th
                        onClick={() => handleSort("state")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        State {renderSortIcon("state")}
                      </th>

                      <th
                        onClick={() => handleSort("createdAt")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Registered On {renderSortIcon("createdAt")}
                      </th>

                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row._id || idx}>
                        <td>{(currentPage - 1) * limit + idx + 1}</td>
                        <td>{getName(row)}</td>
                        <td>{getEmail(row)}</td>
                        <td>{getPhone(row)}</td>
                        <td>{getCompanyName(row)}</td>
                        <td>
                          {(() => {
                            const roleColor = getRoleColor(row.role);

                            return (
                              <span
                                className="status-badge"
                                style={{
                                  backgroundColor: roleColor.bg,
                                  color: roleColor.text,
                                  padding: "8px 15px",
                                  borderRadius: "20px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "70px",
                                  textAlign: "center",
                                }}
                              >
                                {getRole(row)}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${row.status === "active" ? "active" : row.status === "pending" ? "pending" : "inactive"}`}
                          >
                            {getStatus(row)}
                          </span>
                        </td>
                        <td>{getPlan(row)}</td>
                        <td>{getCity(row)}</td>
                        <td>{getState(row)}</td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <button
                              className="action-icon-btn edit"
                              title="Edit"
                              onClick={() =>
                                navigate(`/user-report/${row._id}`)
                              }
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i className="fa-solid fa-pen"></i>
                            </button>

                            {row.status !== "inactive" && (
                              <button
                                className="action-icon-btn delete"
                                type="button"
                                title="Delete"
                                onClick={() => openDeleteModal(row)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>
      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete User</h5>
              <button
                type="button"
                className="crop-close"
                onClick={closeDeleteModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <p className="confirm-message">
                Are you sure want to delete {selectedRow?.name || "this user"}?
              </p>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn confirm-delete-btn"
                onClick={handleDeleteUser}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReport;
